import { useQuery } from '@apollo/client';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { GET_COMPONENTS_LIST } from 'graphql/secure/queries';
import { useLogEvent } from 'hooks/useLogEvent';
import { useMemo, useState } from 'react';
import { IGraphQlConnection } from 'types/core.types';
import { ESortOrder } from 'types/pagination.types';
import {
  ArrayParam,
  createEnumParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';
import { COMPONENTS_PER_PAGE } from 'utils/pagination.utils';

interface IQueryState {
  searchTerm?: string | null;
  partnerId?: string | null;
  rawMaterialIds?: string[] | null;
  orderByMethod?: 'ASC' | 'DESC' | undefined;
  orderByField?: string | undefined;
}

const useComponentItems = ({
  defaultSearchTerm,
  defaultOwnedById,
  defaultRawMaterialIds,
  skipUseQueryParams = false,
  queryAll,
}: {
  defaultSearchTerm?: string;
  defaultOwnedById?: string;
  defaultRawMaterialIds?: string[];
  skipUseQueryParams?: boolean;
  queryAll?: boolean;
} = {}) => {
  const { logEvent } = useLogEvent();

  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, defaultSearchTerm || ''),
      partnerId: withDefault(StringParam, defaultOwnedById),
      rawMaterialIds: withDefault(ArrayParam, defaultRawMaterialIds),
      orderByMethod: withDefault(createEnumParam([ESortOrder.ASC, ESortOrder.DESC]), undefined),
      orderByField: withDefault(StringParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );

  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({
    searchTerm: defaultSearchTerm,
    partnerId: defaultOwnedById,
    rawMaterialIds: defaultRawMaterialIds,
    orderByMethod: undefined,
    orderByField: undefined,
  });

  const { partnerId, rawMaterialIds, orderByMethod, orderByField, searchTerm } = skipUseQueryParams
    ? internalQueryState
    : query;

  const filters: TFilter<EFilterType.PARTNERS | EFilterType.RAW_MATERIALS>[] = useMemo(
    () => [
      {
        type: EFilterType.RAW_MATERIALS,
        value: rawMaterialIds
          ? (rawMaterialIds.filter((id): id is string => id !== null) as string[])
          : undefined,
        multiple: true,
        label: 'Raw materials',
        placeholder: 'All raw materials',
      },
      {
        type: EFilterType.PARTNERS,
        value: partnerId,
        multiple: false,
      },
    ],
    [partnerId, rawMaterialIds]
  );

  const { data, error, loading, refetch, fetchMore } = useQuery<{
    components: IGraphQlConnection<IComponentItem>;
  }>(GET_COMPONENTS_LIST, {
    variables: {
      first: queryAll ? null : COMPONENTS_PER_PAGE,
      filters: {
        searchTerm: searchTerm || defaultSearchTerm,
        ...(partnerId && { ownedById: partnerId }),
        ...(rawMaterialIds && { rawMaterialIds }),
      },
      orderBy: {
        orderByField,
        orderByMethod,
      },
    },
  });

  const hasNextPage: boolean = !!data?.components?.pageInfo?.hasNextPage;
  const endCursor = data?.components?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleSearchTermChange = (searchTerm: string) => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    setter((prev: IQueryState) => ({ ...prev, searchTerm }));
    logEvent('COMPONENT_SEARCHED', { searchTerm });
  };

  const components: IComponentItem[] = useMemo(() => {
    if (data?.components) {
      return removeGraphConnections(data.components);
    }
    return [];
  }, [data?.components]);

  const handleFilterReset = () => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    setter((prev: IQueryState) => ({
      ...prev,
      partnerId: undefined,
      rawMaterialIds: undefined,
    }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    if (event.type === EFilterType.PARTNERS) {
      setter((prev: IQueryState) => ({
        ...prev,
        partnerId: (event.value as string) || undefined,
      }));
    } else if (event.type === EFilterType.RAW_MATERIALS) {
      setter((prev: IQueryState) => ({
        ...prev,
        rawMaterialIds: (event.value as string[]) || undefined,
      }));
    }
  };

  return {
    error,
    loading,
    components,
    searchTerm,
    filters,
    hasNextPage,
    endCursor,
    handleSearchTermChange,
    refetch,
    handlePageEndReached,
    handleFilterReset,
    handleFilterChange,
    setQuery: skipUseQueryParams ? setInternalQueryState : setQuery,
  };
};

export default useComponentItems;
